import { useState, useEffect } from "react";
import { TrendingUp, Users, Target, AlertCircle, IndianRupee, Calendar, PiggyBank, Award, BarChart2, CalendarCheck, UserCheck, ClipboardList, MapPin, CheckCircle, ListTodo, FolderKanban, Clock, Zap, Star, TrendingDown, Activity } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area } from "recharts";
import { useBusiness } from "@/contexts/BusinessContext";
import ManagerDashboard from "@/components/ManagerDashboard";
import EmployeeDashboard from "@/components/EmployeeDashboard";
import AdminDashboard from "@/components/AdminDashboard";
import AdminOverview from "@/components/dashboard/AdminOverview";
import MobileAdminDashboardA from "@/components/mobile/MobileAdminDashboardA";
import { DATE_PRESETS, computeDatePreset } from "@/utils/datePresets";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// LocalStorage key for persisting selected business
const SELECTED_BUSINESS_KEY = "dashboard_selected_business";

// Separate component for Team Leads List
function TeamLeadsList({ data }) {
  if (!data) return null;
  
  const bgColors = ['bg-amber-500', 'bg-slate-400', 'bg-orange-400', 'bg-indigo-400'];
  
  const items = [];
  for (let i = 0; i < data.length; i++) {
    const member = data[i];
    const bgColor = bgColors[i % 4];
    const initial = member.name ? member.name.charAt(0).toUpperCase() : '?';
    
    items.push(
      <div 
        key={member.user_id || i}
        className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:shadow-md transition-all"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${bgColor}`}>
            {initial}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-900 truncate" title={member.name}>
              {member.name}
            </p>
            <div className="flex items-center gap-1 text-sm">
              <span className="text-2xl font-bold text-indigo-600">{member.total_leads}</span>
              <span className="text-slate-500">leads</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return <>{items}</>;
}

// Separate component for City Leads Breakdown
function CityLeadsList({ data, leadsWithoutCity }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-slate-500 py-6">
        <MapPin className="w-10 h-10 mx-auto mb-2 text-slate-300" />
        <p>No city data available yet</p>
        <p className="text-xs mt-1">Add city information to your leads</p>
      </div>
    );
  }
  
  const barColors = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#84cc16', '#22c55e', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1'];
  
  const items = [];
  for (let i = 0; i < data.length; i++) {
    const city = data[i];
    const barColor = barColors[i % barColors.length];
    const maxLeads = data[0]?.total_leads || 1;
    const barWidth = Math.max((city.total_leads / maxLeads) * 100, 5);
    
    items.push(
      <div key={city.city || i} className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-slate-700 truncate max-w-[150px]" title={city.city}>
            {city.city}
          </span>
          <span className="font-bold text-indigo-600">{city.total_leads}</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${barWidth}%`, backgroundColor: barColor }}
          />
        </div>
      </div>
    );
  }
  
  // Add "Others" row if there are leads without city
  if (leadsWithoutCity > 0) {
    items.push(
      <div key="no-city" className="space-y-1 opacity-60">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-slate-500 italic">No City Specified</span>
          <span className="font-bold text-slate-500">{leadsWithoutCity}</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full bg-slate-300"
            style={{ width: '20%' }}
          />
        </div>
      </div>
    );
  }
  
  return <div className="space-y-3">{items}</div>;
}

const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // Active date preset for the pill-row highlight. Cleared when the user
  // manually edits the From/To inputs or hits Clear. Setting a preset
  // immediately triggers analytics refetch — no need to hit Apply.
  const [activeDatePreset, setActiveDatePreset] = useState("");
  
  // Business Switcher State (Admin only)
  const [currentUser, setCurrentUser] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  
  // Import business context
  const { selectedBusiness, selectBusiness } = useBusiness();
  
  // Use context business_id as default, or "all" for admin if no selection
  const [selectedBusinessId, setSelectedBusinessId] = useState(() => {
    // Load from localStorage on mount (for admin switcher persistence)
    const stored = localStorage.getItem(SELECTED_BUSINESS_KEY);
    return stored || (selectedBusiness?.business_id || "all");
  });
  
  // Task Metrics State
  const [taskMetrics, setTaskMetrics] = useState(null);
  
  // Employee Dashboard State
  const [employeeMetrics, setEmployeeMetrics] = useState(null);
  
  // Manager Dashboard State
  const [managerMetrics, setManagerMetrics] = useState(null);
  
  // Admin Dashboard State
  const [adminMetrics, setAdminMetrics] = useState(null);
  
  // Dashboard Card Visibility State
  const [visibleCards, setVisibleCards] = useState([]);

  // Mobile viewport detection — admins on <768px get the redesigned Mobile CRM "Variation A"
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );
  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Sync with BusinessContext when it changes
  useEffect(() => {
    if (selectedBusiness?.business_id && selectedBusinessId !== selectedBusiness.business_id) {
      setSelectedBusinessId(selectedBusiness.business_id);
      localStorage.setItem(SELECTED_BUSINESS_KEY, selectedBusiness.business_id);
    }
  }, [selectedBusiness]);

  // Fetch current user to check if admin
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: "include",
        });
        if (response.ok) {
          const user = await response.json();
          setCurrentUser(user);
          
          // If admin, fetch businesses
          if (user.role === "admin") {
            fetchBusinesses();
          }
          
          // Fetch card visibility settings for the user
          fetchCardVisibility();
        }
      } catch (error) {
        console.error("Error fetching current user:", error);
      }
    };
    fetchCurrentUser();
  }, []);
  
  // Fetch card visibility settings
  const fetchCardVisibility = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/dashboard/card-visibility`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setVisibleCards(data.visible_cards || []);
      }
    } catch (error) {
      console.error("Error fetching card visibility:", error);
    }
  };

  // Fetch businesses for admin switcher
  const fetchBusinesses = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/businesses`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setBusinesses(data);
      }
    } catch (error) {
      console.error("Error fetching businesses:", error);
    }
  };

  // Fetch analytics when business selection changes
  useEffect(() => {
    if (currentUser) {
      fetchAnalytics(startDate, endDate);
    }
  }, [selectedBusinessId, currentUser]);

  const handleBusinessChange = (businessId) => {
    setSelectedBusinessId(businessId);
    // Persist to localStorage
    localStorage.setItem(SELECTED_BUSINESS_KEY, businessId);
    // Update the BusinessContext if a specific business is selected
    if (businessId !== "all") {
      const business = businesses.find(b => b.business_id === businessId);
      if (business) {
        selectBusiness(business);
      }
    }
  };

  const fetchAnalytics = async (start = "", end = "") => {
    setLoading(true);
    try {
      // For employees (non-admin/manager), fetch employee-specific dashboard
      const isEmployeeRole = currentUser && !["super_admin", "admin", "manager"].includes(currentUser.role);
      const isManagerRole = currentUser?.role === "manager";
      const isAdminRole = currentUser?.role === "admin" || currentUser?.role === "super_admin";
      
      if (isEmployeeRole) {
        await fetchEmployeeMetrics();
      } else if (isManagerRole) {
        // Fetch manager-specific dashboard
        await fetchManagerMetrics(start, end);
      } else if (isAdminRole) {
        // Fetch admin-specific dashboard (Super Admin or Admin)
        await fetchAdminMetrics(start, end);
      } else {
        // Fallback to regular analytics
        let url = `${BACKEND_URL}/api/analytics/dashboard`;
        const params = new URLSearchParams();
        if (start) params.append("start_date", start);
        if (end) params.append("end_date", end);
        
        if (currentUser?.role in ["super_admin", "admin"] && selectedBusinessId) {
          params.append("business_id", selectedBusinessId);
        }
        
        if (params.toString()) url += `?${params.toString()}`;
        
        const response = await fetch(url, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setAnalytics(data);
        }
        
        await fetchTaskMetrics();
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeMetrics = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/dashboard/employee`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setEmployeeMetrics(data);
      }
    } catch (error) {
      console.error("Error fetching employee metrics:", error);
    }
  };

  const fetchManagerMetrics = async (start = "", end = "") => {
    try {
      let url = `${BACKEND_URL}/api/dashboard/manager`;
      const params = new URLSearchParams();
      if (start) params.append("start_date", start);
      if (end) params.append("end_date", end);
      if (selectedBusinessId && selectedBusinessId !== "all") {
        params.append("business_id", selectedBusinessId);
      }
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setManagerMetrics(data);
      }
    } catch (error) {
      console.error("Error fetching manager metrics:", error);
    }
  };

  const fetchAdminMetrics = async (start = "", end = "") => {
    try {
      let url = `${BACKEND_URL}/api/dashboard/admin`;
      const params = new URLSearchParams();
      if (start) params.append("start_date", start);
      if (end) params.append("end_date", end);
      if (selectedBusinessId && selectedBusinessId !== "all") {
        params.append("business_id", selectedBusinessId);
      } else if (selectedBusinessId === "all" && currentUser?.role === "super_admin") {
        params.append("business_id", "all");
      }
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setAdminMetrics(data);
      } else {
        console.error("Admin metrics fetch failed:", response.status);
      }
    } catch (error) {
      console.error("Error fetching admin metrics:", error);
    }
  };

  const fetchTaskMetrics = async () => {
    try {
      let url = `${BACKEND_URL}/api/tasks/metrics`;
      if (currentUser?.role === "admin" && selectedBusinessId && selectedBusinessId !== "all") {
        url += `?business_id=${selectedBusinessId}`;
      }
      
      const response = await fetch(url, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setTaskMetrics(data);
      }
    } catch (error) {
      console.error("Error fetching task metrics:", error);
    }
  };

  const handleFilterApply = () => {
    fetchAnalytics(startDate, endDate);
  };

  // Preset click → compute range → set both fields → refetch immediately.
  const handleDatePreset = (preset) => {
    const [s, e] = computeDatePreset(preset);
    setStartDate(s);
    setEndDate(e);
    setActiveDatePreset(preset);
    fetchAnalytics(s, e);
  };

  // Get selected business name for display
  const getSelectedBusinessName = () => {
    if (selectedBusinessId === "all") return "All Businesses";
    const business = businesses.find(b => b.business_id === selectedBusinessId);
    return business?.name || "Select Business";
  };

  const isSuperAdmin = currentUser?.role === "super_admin";
  const isAdmin = currentUser?.role === "admin" || isSuperAdmin;
  const isManager = currentUser?.role === "manager";
  const isEmployee = currentUser && !["super_admin", "admin", "manager"].includes(currentUser.role);

  const handleFilterClear = () => {
    setStartDate("");
    setEndDate("");
    setActiveDatePreset("");
    fetchAnalytics();
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return "₹0";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Admins (desktop) get the Leads / Attendance / Tasks / Employees overview. The
  // super-admin "all businesses" view keeps the aggregate dashboard below.
  if (isAdmin && !isMobileViewport && !(isSuperAdmin && selectedBusinessId === "all")) {
    return (
      <AdminOverview
        currentUser={currentUser}
        businessId={selectedBusinessId && selectedBusinessId !== "all" ? selectedBusinessId : currentUser?.business_id}
        businessName={getSelectedBusinessName()}
      />
    );
  }

  if (loading && !analytics) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded-lg w-48"></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-slate-200 rounded-2xl"></div>
          ))}
        </div>
      </div>
    );
  }

  const stageData = analytics?.stage_counts
    ? Object.entries(analytics.stage_counts)
        .filter(([_, value]) => value > 0)
        .map(([name, value]) => ({
          name: name.length > 12 ? name.substring(0, 12) + "..." : name,
          fullName: name,
          value,
        }))
    : [];

  const COLORS = ["#6366F1", "#EC4899", "#8B5CF6", "#10B981", "#F59E0B", "#EF4444", "#06B6D4", "#84CC16", "#F97316", "#64748B"];

  // Prepare source revenue data
  const sourceRevenueData = analytics?.source_revenue || [];
  
  // Prepare conversion trends data
  const conversionTrendsData = (analytics?.conversion_trends || []).map(item => ({
    ...item,
    month: item.month.substring(5), // Show only MM format
  }));
  
  // Prepare team performance data - Use leaderboard data which includes task completion
  const teamPerformanceData = analytics?.team_performance_leaderboard || analytics?.team_performance || [];

  // ════════ Mobile-native admin dashboard ════════
  // When viewport < 768px AND user is admin/super_admin AND admin metrics have
  // loaded, render the "Variation A · Vibrant" mobile design directly. The
  // parent header + Date Filter are NOT rendered because the mobile design
  // has its own integrated app bar + greeting + month chip.
  if (
    isMobileViewport &&
    (currentUser?.role === "admin" || currentUser?.role === "super_admin") &&
    adminMetrics
  ) {
    return (
      <MobileAdminDashboardA
        adminMetrics={adminMetrics}
        currentUser={currentUser}
        businessName={getSelectedBusinessName()}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-heading font-bold text-slate-900 dark:text-white tracking-tight">
            Dashboard
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">
            Welcome back! Here&apos;s your overview.
            {isAdmin && selectedBusinessId !== "all" && (
              <span className="ml-2 text-indigo-600 font-medium">
                Viewing: {getSelectedBusinessName()}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Date Range Filter */}
      <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
        <CardContent className="p-4 space-y-3">
          {/* Quick preset row */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 text-slate-600 mr-1">
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Date Filter</span>
            </div>
            {DATE_PRESETS.map(p => {
              const on = activeDatePreset === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => handleDatePreset(p.key)}
                  data-testid={`date-preset-${p.key}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                    on
                      ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                      : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-700"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
            {activeDatePreset && (
              <button
                type="button"
                onClick={handleFilterClear}
                className="text-xs font-semibold text-slate-500 hover:text-rose-600 ml-1"
              >
                Clear
              </button>
            )}
          </div>

          {/* Custom range row */}
          <div className="flex flex-col sm:flex-row items-end gap-4 pt-1 border-t border-slate-100">
            <div className="flex-1 min-w-[140px]">
              <Label htmlFor="start-date" className="text-xs text-slate-500">From</Label>
              <Input
                id="start-date"
                type="date"
                data-testid="date-filter-start"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setActiveDatePreset(""); }}
                className="bg-slate-50 border border-slate-200 rounded-xl"
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label htmlFor="end-date" className="text-xs text-slate-500">To</Label>
              <Input
                id="end-date"
                type="date"
                data-testid="date-filter-end"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setActiveDatePreset(""); }}
                className="bg-slate-50 border border-slate-200 rounded-xl"
              />
            </div>
            <Button
              onClick={handleFilterApply}
              data-testid="apply-date-filter"
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-4"
            >
              Apply
            </Button>
            {(startDate || endDate) && (
              <Button
                onClick={handleFilterClear}
                variant="outline"
                className="rounded-xl border-slate-200"
              >
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Employee Dashboard - Only for non-admin/manager users */}
      {isEmployee && employeeMetrics ? (
        <EmployeeDashboard employeeMetrics={employeeMetrics} currentUser={currentUser} businessId={selectedBusinessId !== "all" ? selectedBusinessId : currentUser?.business_id} />
      ) : isManager && managerMetrics ? (
        <ManagerDashboard managerMetrics={managerMetrics} currentUser={currentUser} businessId={selectedBusinessId !== "all" ? selectedBusinessId : currentUser?.business_id} />
      ) : isAdmin && adminMetrics ? (
        <AdminDashboard adminMetrics={adminMetrics} currentUser={currentUser} visibleCards={visibleCards} businessId={selectedBusinessId !== "all" ? selectedBusinessId : null} />
      ) : !isEmployee && !isManager && !isAdmin && (
        <>
      {/* Revenue Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card
          data-testid="total-revenue-card"
          className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl shadow-xl shadow-emerald-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80 font-medium">Total Revenue Generated</p>
                <h3 className="text-3xl font-heading font-bold mt-2">
                  {formatCurrency(analytics?.total_revenue)}
                </h3>
                <p className="text-xs text-white/60 mt-1">From Won/Converted deals</p>
              </div>
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <IndianRupee className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          data-testid="expected-revenue-card"
          className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-2xl shadow-xl shadow-amber-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80 font-medium">Expected Revenue</p>
                <h3 className="text-3xl font-heading font-bold mt-2">
                  {formatCurrency(analytics?.expected_revenue)}
                </h3>
                <p className="text-xs text-white/60 mt-1">From active pipeline</p>
              </div>
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <PiggyBank className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Metrics Cards - Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card
          data-testid="total-leads-card"
          className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all hover:-translate-y-1 duration-300"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">Total Leads</p>
                <h3 className="text-3xl font-heading font-bold text-slate-900 mt-2">
                  {analytics?.total_leads || 0}
                </h3>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          data-testid="conversion-rate-card"
          className="bg-gradient-to-br from-indigo-600 to-violet-700 text-white rounded-2xl shadow-xl shadow-indigo-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80 font-medium">Conversion Rate</p>
                <h3 className="text-3xl font-heading font-bold mt-2">
                  {analytics?.conversion_rate || 0}%
                </h3>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          data-testid="new-leads-card"
          className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all hover:-translate-y-1 duration-300"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">New Leads</p>
                <h3 className="text-3xl font-heading font-bold text-slate-900 mt-2">
                  {analytics?.stage_counts?.["New Lead"] || 0}
                </h3>
              </div>
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-pink-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          data-testid="tasks-due-card"
          className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all hover:-translate-y-1 duration-300"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">Tasks Due Today</p>
                <h3 className="text-3xl font-heading font-bold text-slate-900 mt-2">
                  {analytics?.tasks_due_today || 0}
                </h3>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* New Summary Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Today's Meetings & Follow-ups */}
        <Card
          data-testid="todays-meetings-card"
          className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white rounded-2xl shadow-xl shadow-cyan-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80 font-medium">Today&apos;s Meetings</p>
                <h3 className="text-3xl font-heading font-bold mt-2">
                  {analytics?.todays_meetings_count || 0}
                </h3>
                <p className="text-xs text-white/60 mt-1">Scheduled for today</p>
              </div>
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <CalendarCheck className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Unfinished Tasks Today */}
        <Card
          data-testid="unfinished-tasks-card"
          className="bg-gradient-to-br from-rose-500 to-pink-600 text-white rounded-2xl shadow-xl shadow-rose-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80 font-medium">Unfinished Tasks</p>
                <h3 className="text-3xl font-heading font-bold mt-2">
                  {analytics?.unfinished_tasks_today || 0}
                </h3>
                <p className="text-xs text-white/60 mt-1">Due today and pending</p>
              </div>
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <ClipboardList className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Members Count */}
        <Card
          data-testid="team-leads-count-card"
          className="bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-2xl shadow-xl shadow-violet-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white/80 font-medium">Active Team Members</p>
                <h3 className="text-3xl font-heading font-bold mt-2">
                  {analytics?.team_leads_breakdown?.length || 0}
                </h3>
                <p className="text-xs text-white/60 mt-1">With assigned leads</p>
              </div>
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <UserCheck className="w-7 h-7 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Task Management Metrics */}
      {taskMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Total Tasks Assigned */}
          <Card
            data-testid="total-tasks-assigned-card"
            className="bg-gradient-to-br from-indigo-500 to-blue-600 text-white rounded-2xl shadow-xl shadow-indigo-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80 font-medium">My Tasks</p>
                  <h3 className="text-3xl font-heading font-bold mt-2">
                    {taskMetrics?.my_tasks || 0}
                  </h3>
                  <p className="text-xs text-white/60 mt-1">Assigned to you</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <ListTodo className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Incomplete Tasks */}
          <Card
            data-testid="incomplete-tasks-card"
            className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-2xl shadow-xl shadow-amber-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80 font-medium">Total Incomplete</p>
                  <h3 className="text-3xl font-heading font-bold mt-2">
                    {taskMetrics?.total_incomplete || 0}
                  </h3>
                  <p className="text-xs text-white/60 mt-1">To Do + In Progress</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Completed Tasks */}
          <Card
            data-testid="completed-tasks-card"
            className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl shadow-xl shadow-emerald-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80 font-medium">Total Completed</p>
                  <h3 className="text-3xl font-heading font-bold mt-2">
                    {taskMetrics?.total_completed || 0}
                  </h3>
                  <p className="text-xs text-white/60 mt-1">Tasks done</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Overdue Tasks */}
          <Card
            data-testid="overdue-tasks-card"
            className="bg-gradient-to-br from-red-500 to-rose-600 text-white rounded-2xl shadow-xl shadow-red-500/20 border border-white/10 hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80 font-medium">Overdue Tasks</p>
                  <h3 className="text-3xl font-heading font-bold mt-2">
                    {taskMetrics?.overdue_tasks || 0}
                  </h3>
                  <p className="text-xs text-white/60 mt-1">Past due date</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team Leads Breakdown Card */}
      {analytics?.team_leads_breakdown && analytics.team_leads_breakdown.length > 0 && (
        <Card
          data-testid="team-leads-breakdown-card"
          className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
        >
          <CardHeader>
            <CardTitle className="text-xl font-heading font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-600" />
              Individual Total Leads by Team Member
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <TeamLeadsList data={analytics.team_leads_breakdown} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* City-Wise Leads Breakdown Card */}
      <Card
        data-testid="city-leads-breakdown-card"
        className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      >
        <CardHeader>
          <CardTitle className="text-xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-rose-600" />
            Total Leads by City
            {analytics?.city_leads_breakdown?.length > 0 && (
              <span className="text-sm font-normal text-slate-500 ml-2">
                ({analytics.city_leads_breakdown.length} cities)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-h-[400px] overflow-y-auto pr-2">
            <CityLeadsList 
              data={analytics?.city_leads_breakdown || []} 
              leadsWithoutCity={analytics?.leads_without_city || 0}
            />
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card
          data-testid="lead-stages-chart"
          className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
        >
          <CardHeader>
            <CardTitle className="text-xl font-heading font-bold text-slate-900">
              Leads by Stage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#64748B" fontSize={10} />
                <YAxis stroke="#64748B" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1E293B",
                    border: "none",
                    borderRadius: "0.75rem",
                    color: "#fff",
                  }}
                  formatter={(value, name, props) => [value, props.payload.fullName]}
                />
                <Bar dataKey="value" fill="#6366F1" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card
          data-testid="lead-distribution-chart"
          className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
        >
          <CardHeader>
            <CardTitle className="text-xl font-heading font-bold text-slate-900">
              Lead Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stageData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ""
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stageData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1E293B",
                    border: "none",
                    borderRadius: "0.75rem",
                    color: "#fff",
                  }}
                  formatter={(value, name, props) => [value, props.payload.fullName]}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Conversion Trends Chart */}
      {conversionTrendsData.length > 0 && (
        <Card
          data-testid="conversion-trends-chart"
          className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
        >
          <CardHeader>
            <CardTitle className="text-xl font-heading font-bold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              Conversion Trends (Last 6 Months)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={conversionTrendsData}>
                <defs>
                  <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorConverted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1E293B",
                    border: "none",
                    borderRadius: "0.75rem",
                    color: "#fff",
                  }}
                  formatter={(value, name) => {
                    if (name === "total_leads") return [value, "Total Leads"];
                    if (name === "converted") return [value, "Converted"];
                    if (name === "conversion_rate") return [`${value}%`, "Conversion Rate"];
                    return [value, name];
                  }}
                />
                <Legend />
                <Area type="monotone" dataKey="total_leads" stroke="#6366F1" fillOpacity={1} fill="url(#colorLeads)" name="Total Leads" />
                <Area type="monotone" dataKey="converted" stroke="#10B981" fillOpacity={1} fill="url(#colorConverted)" name="Converted" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Source Revenue & Team Performance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Source-wise Revenue */}
        {sourceRevenueData.length > 0 && (
          <Card
            data-testid="source-revenue-chart"
            className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          >
            <CardHeader>
              <CardTitle className="text-xl font-heading font-bold text-slate-900 flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-emerald-600" />
                Revenue by Source
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sourceRevenueData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" stroke="#64748B" tickFormatter={(value) => `₹${(value/1000).toFixed(0)}k`} />
                  <YAxis dataKey="source" type="category" stroke="#64748B" fontSize={11} width={80} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1E293B",
                      border: "none",
                      borderRadius: "0.75rem",
                      color: "#fff",
                    }}
                    formatter={(value, name) => {
                      if (name === "revenue") return [formatCurrency(value), "Revenue"];
                      if (name === "deals") return [value, "Deals Won"];
                      return [value, name];
                    }}
                  />
                  <Bar dataKey="revenue" fill="#10B981" radius={[0, 8, 8, 0]} name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Team Performance */}
        {teamPerformanceData.length > 0 && (
          <Card
            data-testid="team-performance-chart"
            className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          >
            <CardHeader>
              <CardTitle className="text-xl font-heading font-bold text-slate-900 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-600" />
                Team Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[350px] overflow-y-auto">
                {teamPerformanceData.map((member, index) => (
                  <div key={member.name || member.user_id} className={`flex items-center gap-4 p-3 rounded-xl transition-all ${
                    member.is_best_performer ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50 hover:bg-slate-100'
                  }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                      index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-orange-400' : 'bg-slate-300'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900 truncate">{member.name}</p>
                        {member.is_best_performer && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">Top</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-1">
                        {/* Task Performance */}
                        <span className="flex items-center gap-1">
                          <span className={`font-semibold ${
                            (member.task_completion_rate || 0) >= 80 ? 'text-emerald-600' : 
                            (member.task_completion_rate || 0) >= 50 ? 'text-amber-600' : 'text-red-500'
                          }`}>
                            {member.task_completion_rate || 0}%
                          </span>
                          <span>Tasks</span>
                          <span className="text-slate-400">({member.tasks_completed || 0}/{member.total_tasks || 0})</span>
                        </span>
                        {/* Lead Performance */}
                        {(member.total_leads > 0 || member.has_leads) && (
                          <>
                            <span className="text-slate-300">|</span>
                            <span className="flex items-center gap-1">
                              <span className="text-indigo-600 font-semibold">{member.lead_conversion_rate || member.conversion_rate || 0}%</span>
                              <span>Leads</span>
                              <span className="text-slate-400">({member.leads_converted || member.converted || 0}/{member.total_leads || 0})</span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right min-w-[60px]">
                      <div className="flex items-center justify-end gap-1">
                        <TrendingUp className="w-4 h-4 text-indigo-500" />
                        <p className="font-bold text-indigo-600">{member.combined_score || member.conversion_rate || 0}%</p>
                      </div>
                      <p className="text-xs text-slate-400">Score</p>
                    </div>
                  </div>
                ))}
                {teamPerformanceData.length === 0 && (
                  <p className="text-center text-slate-500 py-8">No team data available</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
